/**
 * Fragmento novo do payload de gravacao da meta: a camada opcional da meta
 * do colaborador, introduzida pela campanha de cotas de consorcio.
 *
 * A meta do modelo (Tipo de Meta, Tipo de Calculo, Tipo de Pagamento, Qtde
 * Meta + Bonus por modelo) e sempre informada e ja vai no payload padrao,
 * fora deste arquivo — nao depende de nada aqui.
 *
 * A meta do colaborador e independente: quando ligada, soma o realizado de
 * todos os modelos e libera um bonus proprio (id_origem_bonus). Quando
 * desligada, este fragmento nao emite nada alem dos campos que descrevem o
 * objetivo — nao ha bloco novo para quem nao usa a funcionalidade.
 *
 * Vive fora do componente porque e logica pura e precisa de teste — o
 * builder do payload principal continua em ModalCadastroMeta.salvarNovaMeta.
 */

/**
 * @param {Object} form - novaMetaForm do wizard
 * @returns {Object} fragmento para espalhar no payload
 */
export function montarExtrasMeta(form) {
  const extras = {
    id_origem_conteudo: Number(form.origemConteudo) || 1,
    usa_meta_colaborador: form.usaMetaColaborador ? "S" : "N"
  };

  if (!form.usaMetaColaborador) {
    return extras;
  }

  extras.id_origem_meta = Number(form.origemMeta) || 1;

  if (Number(form.origemMeta) === 2) {
    // 1 Fixo, 2 Progressivo por faixa — mesma convencao numerica de
    // tiposCalculo/tiposPagamento (calculoMixin.js), nao 'F'/'P' em texto.
    const tipoCrescimento = Number(form.tipoCrescimentoMedia) || 1;
    const progressivo = tipoCrescimento === 2;

    extras.media = {
      campo_referencia: "Q",
      // Base da media espelha sempre a origem do objetivo (Veiculo/Cota) —
      // derivado, nunca escolhido pelo usuario, para nao permitir calcular
      // a meta sobre um realizado diferente do que a campanha mede.
      tipo_referencia: Number(form.origemConteudo) === 2 ? "C" : "V",
      id_tipo_crescimento: tipoCrescimento,
      valor_minimo: parseFloat(form.valorMinimoMeta) || 0,
      periodos: (form.mesesReferencia || []).map(m => ({
        ano_ref: m.ano_ref,
        mes_ref: m.mes_ref
      }))
    };

    // percentual_crescimento (Fixo) e faixas (Progressivo) sao mutuamente
    // exclusivos no validador da API (prohibited_if) — e prohibited_if
    // reprova a simples PRESENCA do campo, 0 incluso (0 nao e "vazio" pra
    // validacao do Laravel, diferente de array vazio). Por isso a chave
    // nem entra no objeto do lado que nao se aplica, em vez de ir com 0/[]
    // — mesmo padrao que bonus_colaborador ja usa abaixo.
    if (progressivo) {
      extras.media.faixas = (form.faixasCrescimento || []).map(f => ({
        faixa_de: parseFloat(f.de),
        // "ate" vazio so e valido na ultima faixa (sem teto) — o modal
        // ja bloqueia salvar com "ate" vazio em qualquer faixa que nao
        // seja a ultima (ver faixasCrescimentoUtils.pendenciasFaixasCrescimento).
        faixa_ate: f.ate === "" || f.ate == null ? null : parseFloat(f.ate),
        percentual_crescimento: parseFloat(f.crescimento) || 0
      }));
    } else {
      extras.media.percentual_crescimento = parseFloat(form.percentualCrescimento) || 0;
    }
  }

  const metas = form.metasCalculadas || {};

  // Bonus por participante, so na origem "% sobre referencia" (Fase 5): o valor
  // em reais passa a ser gravado, para a listagem e a edicao nao terem que
  // buscar a comissao P2 de novo so para exibir. E CACHE — a apuracao recalcula
  // da comissao do momento e sobrescreve; nenhum pagamento sai daqui.
  //
  // Na origem "Informado" sai null de proposito: o valor e igual para todos e ja
  // vai em bonus_colaborador.valor_bonus logo abaixo. Gravar nas duas pontas
  // criaria duas versoes do mesmo numero, livres para divergir.
  //
  // form.comissaoParticipantes e null enquanto a comissao NAO foi buscada, e um
  // objeto depois de uma busca bem-sucedida (ver TabelaMetasCalculadas). A
  // diferenca importa: buscado e o colaborador ausente do mapa significa
  // comissao zero — um valor calculado, gravavel como 0. Nao buscado nao e zero,
  // e sim desconhecido. Gravar 0 nos dois casos faria "ainda nao calculei"
  // parecer "calculei e deu zero".
  //
  // Sem comissao fresca, NAO manda null: reenvia o valor que veio do banco
  // (registro.bonusGravado, ver montarMetasCalculadas). Null aqui APAGA o valor
  // gravado, e isso acontecia num caso banal — abrir a meta para editar outra
  // coisa e salvar com a requisicao da comissao ainda em voo. O valor bom sumia
  // sem aviso. Um numero defasado (a apuracao o corrige) e melhor do que
  // nenhum. Null so vai quando nao ha nada a preservar: meta nova.
  //
  // Preservar e responsabilidade DAQUI, nao da API: quem sabe o que carregou da
  // tela e o front. A API continua gravando exatamente o que recebe — se ela
  // tambem tentasse preservar, nao haveria como limpar o cache nunca.
  const porReferencia = Number(form.origemBonusColaborador) === 2;
  const comissoes = form.comissaoParticipantes;
  const buscouComissao = porReferencia && comissoes != null;
  const percentualBonus = parseFloat(form.comissaoPercentual) || 0;

  extras.metas_colaborador = (form.colaboradoresSelecionados || []).map(c => {
    const registro = metas[c.id_colaborador] || {};
    const bonusGravado = registro.bonusGravado != null ? Number(registro.bonusGravado) : null;
    return {
      id_colaborador: c.id_colaborador,
      id_empresa: c.id_empresa,
      valor_meta: parseFloat(registro.meta) || 0,
      valor_bonus: !porReferencia
        ? null
        : buscouComissao
          ? (percentualBonus / 100) * (parseFloat(comissoes[String(c.id_colaborador)]) || 0)
          : bonusGravado,
      // Ausencia de media (origem Informada nunca tem, e origem Media
      // historica pode nao ter para um colaborador especifico) precisa
      // sair null, nao 0 — um zero de verdade e indistinguivel de "sem
      // media" se os dois virarem o mesmo numero. So convertemos pra
      // numero quando ha de fato algo pra converter.
      valor_media_referencia: registro.media != null && registro.media !== ""
        ? (parseFloat(registro.media) || 0)
        : null,
      meta_manual: registro.manual ? "S" : "N"
    };
  });

  // Bonus do colaborador espelha a origem: informado tem valor_bonus,
  // percentual tem referencia + percentual — nunca os dois juntos.
  const idOrigemBonus = Number(form.origemBonusColaborador) || 1;
  extras.bonus_colaborador = { id_origem_bonus: idOrigemBonus };
  if (idOrigemBonus === 1) {
    extras.bonus_colaborador.valor_bonus = parseFloat(form.valorBonusColaborador) || 0;
  } else {
    extras.bonus_colaborador.referencia = form.comissaoReferencia;
    extras.bonus_colaborador.percentual = parseFloat(form.comissaoPercentual) || 0;
  }

  return extras;
}
